import React from 'react';
import { Star, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';

export const Reviews = () => {
  const getPlatformBadge = (platform) => {
    if (platform === 'google') {
      return (
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
          <img 
            src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
            alt="Google" 
            className="h-5"
          />
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          <span className="text-sm font-semibold text-gray-800">Facebook</span>
        </div>
      );
    }
  };

  return (
    <section id="reviews" className="py-24 bg-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-purple-600 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-pink-600 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Vad Våra <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Kunder Säger</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Riktiga recensioner från våra nöjda kunder
          </p>
        </div>

        {/* Rating Badges */}
        <div className="flex flex-wrap justify-center gap-6 mb-12">
          {/* Google Rating */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm px-8 py-4 rounded-2xl border border-gray-700/50 flex items-center gap-4 hover:border-purple-500/50 transition-all duration-300">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
              alt="Google" 
              className="h-7"
            />
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-white">5.0</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`google-${i}`} size={20} className="text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          </div>

          {/* Facebook Rating */}
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm px-8 py-4 rounded-2xl border border-gray-700/50 flex items-center gap-4 hover:border-purple-500/50 transition-all duration-300">
            <svg className="h-7 w-7" viewBox="0 0 24 24" fill="#1877F2">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-white">5.0</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`fb-${i}`} size={20} className="text-yellow-400 fill-current" />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.id}
              className="group bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 hover:-translate-y-2 relative"
            >
              {/* Platform Badge */}
              <div className="mb-6">
                {getPlatformBadge(testimonial.platform)}
              </div>

              {/* Quote Icon */}
              <div className="mb-4">
                <Quote size={32} className="text-purple-400" />
              </div>

              {/* Rating Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} className="text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Review Text */}
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                "{testimonial.text}"
              </p>

              {/* Verified Badge */}
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Verifierad recension</span>
              </div>

              {/* Hover Accent */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-2xl"></div>
            </div>
          ))}
        </div>

        {/* Trust Message */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-lg">
            Alla recensioner är äkta och från verifierade kunder på Google och Facebook
          </p>
        </div>
      </div>
    </section>
  );
};
